import { getTabId } from "utils/tabId"
import { getStoreData, Stores, updateData } from "services/indexedDB"
import { clearToastProgress, showToastProgress } from "./toastProgress"
import {
  DataRequestDownloadOneFile,
  handleDownloadEncrypted,
  handleDownloadOneFile,
  handleUploadFile,
} from "./handleDownloadFile"
import { ACTION_DOWNLOAD_WORKER } from "./download-worker"
import { includes, isUndefined, omit, random } from "lodash"
import Axios from "axios"
import { ACTION_WORKER } from "workers/type"
import { configureStore } from "stores/configureStore"
import { finishUpload, startUpload } from "reducers/upload-file"
import { runtimeKeyStore } from "stores/runtimeKeyStore"
import {
  delay,
  getParameterByName,
  getPrivateKeyByUserId,
  umamiTracking,
} from "helpers/utils"
import { EVENT } from "constants/events"
import {
  generateTRCCode,
  captureDownloadEvent,
  addBreadcrumb,
  getSentryUserContext,
} from "helpers/sentryMonitoring"
import { API_URL } from "config/environments"
import { encryptionGCMHelper } from "helpers/encryption-gcm"
import { getAccessTokenInLocalStorage } from "helpers/access_token"
import { clearLocalStorage, getUserId } from "helpers/local_storage"

interface WorkerDownloadError {
  phase:
    | "FETCH_FILE"
    | "FETCH_HASH"
    | "DECRYPT_GCM"
    | "DECRYPT_CBC"
    | "VERIFY_SIGNATURE"
    | "DECRYPT_MESSAGE"
    | "ZIP"
    | "INIT"
  message: string
  file_url?: string
  http_status?: number
  file_index?: number
  skipped_files?: string[]
}

export enum UploadFileType {
  GERBER = "GERBER",
  PCB_BOM = "PCB_BOM",
  PCB_STACKUP = "PCB_STACKUP",
  PCB_ASSEMBLY = "PCB_ASSEMBLY",
  PCB_ATTACHMENT = "PCB_ATTACHMENT",
  MECHANICAL_3D = "MECHANICAL_3D",
  MECHANICAL_BOM = "MECHANICAL_BOM",
  MECHANICAL_ATTACHMENT = "MECHANICAL_ATTACHMENT",
  BOM = "BOM",
  OTHER_ATTACHMENT = "OTHER_ATTACHMENT",
}

export interface DownloadQueueItem {
  apiURL: string
  name: string
  prefixStart?: string
  subName: string
  downloadId: string
  buildId?: string
  done?: boolean
  errorMessage?: string
  isFileOne?: boolean
  dataFileOne?: DataRequestDownloadOneFile
  dataBlob?: Blob
  fileHashKeyFromServer?: {
    hash_key: string
    public_key: string
  }
  multipleFile?: any
  fileFolderName?: string
  processMessage?: string
  dataUploading?: any
  dbKey?: string
}

function getComponentIdFromDownloadApiUrl(apiURL: string): string | undefined {
  const m = apiURL.match(/project-component\/history\/([^/?]+)/)
  return m?.[1]
}

function getDownloadEventPair(item: DownloadQueueItem): [string, string] {
  const inviteeKey = item.dataFileOne?.dbKey
  if (inviteeKey) {
    return [EVENT.DOWNLOAD.INVITEE_SUCCESS, EVENT.DOWNLOAD.INVITEE_FAILURE]
  }

  if (item.apiURL?.includes("project-component/history")) {
    return [EVENT.DOWNLOAD.COMPONENT_SUCCESS, EVENT.DOWNLOAD.COMPONENT_FAILURE]
  }
  if (item.apiURL?.includes("conversation_id")) {
    return [
      EVENT.DOWNLOAD.CONVERSATION_SUCCESS,
      EVENT.DOWNLOAD.CONVERSATION_FAILURE,
    ]
  }
  if (item.isFileOne) {
    return [EVENT.DOWNLOAD.FILE_SUCCESS, EVENT.DOWNLOAD.FILE_FAILURE]
  }
  return [EVENT.DOWNLOAD.BUILD_SUCCESS, EVENT.DOWNLOAD.BUILD_FAILURE]
}

function buildDownloadTrackingPayload(
  item: DownloadQueueItem,
  extras: { error?: string; file_count?: number }
): Record<string, unknown> {
  const conversation_id =
    getParameterByName("conversation_id", item.apiURL) || undefined
  const project_id = getParameterByName("project_id", item.apiURL) || undefined
  return {
    build_id: item.buildId,
    conversation_id,
    component_id: getComponentIdFromDownloadApiUrl(item.apiURL),
    project_id,
    file_name: item.isFileOne ? item.name : undefined,
    ...extras,
  }
}

class DownloadQueueManager {
  private queue: DownloadQueueItem[] = []
  private readonly tabId = getTabId()
  private readonly storageKey = `downloadQueue-${this.tabId}`
  private workerDownload: Worker | null = null
  private workerUpload: Worker | null = null

  constructor() {
    // this.restoreQueue()
    this.actionItem = this.actionItem.bind(this)
    this.setupBeforeUnloadWarning()
  }

  async add(item: DownloadQueueItem) {
    if (!isUndefined(item.dataUploading)) {
      await this.initWorkerUpload()
      if (
        includes(
          [
            UploadFileType.OTHER_ATTACHMENT,
            UploadFileType.MECHANICAL_ATTACHMENT,
            UploadFileType.PCB_ATTACHMENT,
          ],
          item.dataUploading.type
        ) &&
        !isUndefined(item.dataUploading.fileList) &&
        item.dataUploading.fileList.length
      ) {
        for (const file of item.dataUploading.fileList) {
          const dataItem = {
            ...item,
            name: file.name,
            downloadId: `${item.downloadId}-${file.name}`,
            dataUploading: {
              ...omit(item.dataUploading, ["fileList"]),
              file: file,
            },
            processMessage: "The file is being loaded and decrypted...",
          }
          configureStore.dispatch(startUpload(dataItem.downloadId))
          this.queue.push(dataItem)
          showToastProgress(
            this.storageKey,
            this.queue,
            this.actionItem,
            random(0, 15),
            dataItem.downloadId
          )
        }
        const fileListSorted = item.dataUploading.fileList.sort(
          (a, b) => a.size - b.size
        )

        for (const fileSort of fileListSorted) {
          const dataItem = this.queue.find(
            (i) => i.downloadId === `${item.downloadId}-${fileSort.name}`
          )
          if (dataItem) {
            await this.startWorkerUploadJob(dataItem)
            await delay(4000) // Delay to avoid overloading the worker
          }
        }
      } else {
        this.queue.push({
          ...item,
          processMessage: "The file is being loaded and decrypted...",
        })
        await this.startWorkerUploadJob(item)
        showToastProgress(
          this.storageKey,
          this.queue,
          this.actionItem,
          random(0, 15),
          item.downloadId
        )
      }
    } else {
      addBreadcrumb("download", "Item queued", "info", {
        file_name: item.name,
        build_id: item.buildId,
      })
      this.queue.push({
        ...item,
        processMessage: "Download file and signature from server...",
      })
      showToastProgress(
        this.storageKey,
        this.queue,
        this.actionItem,
        random(0, 15),
        item.downloadId
      )
      await this.initWorkerDownload()
      try {
        await this.startWorkerJob(item)
      } catch (error: any) {
        const trcCode = generateTRCCode("DWL")
        captureDownloadEvent(`Worker job start failed - ${trcCode}`, {
          error_category: "DWL",
          error_type: "WORKER_JOB_START_FAILURE",
          ...getSentryUserContext(),
          error_code: trcCode,
          step_trail: "downloadQueue → add → startWorkerJob",
          step_failed: error?.message,
          file_name: item.name,
          build_id: item.buildId,
        })
        item.done = false
        item.errorMessage = "An unexpected error occurred. Please try again."
        showToastProgress(this.storageKey, this.queue, this.actionItem)
        this.emitDownloadJobOutcome(item, {
          success: false,
          errorMessage: item.errorMessage,
        })
      }
    }
    // await this.persistQueue()
  }

  private async initWorkerDownload() {
    if (this.workerDownload) return

    this.workerDownload = new Worker(
      new URL("./download-worker.ts", import.meta.url),
      {
        type: "module",
      }
    )
    this.workerDownload.onmessage = async (e) =>
      this.handleWorkerDownloadMessage(e)
    this.workerDownload.onerror = (event) => {
      const trcCode = generateTRCCode("DWL")
      captureDownloadEvent(`Worker uncaught error - ${trcCode}`, {
        error_category: "DWL",
        error_type: "WORKER_UNCAUGHT_ERROR",
        ...getSentryUserContext(),
        error_code: trcCode,
        step_trail: "downloadQueue → initWorkerDownload → onerror",
        step_failed: event.message,
      })
    }
  }

  private async initWorkerUpload() {
    if (this.workerUpload) return

    this.workerUpload = new Worker(
      new URL("../../workers/uploadFileWorker.ts", import.meta.url),
      {
        type: "module",
      }
    )
    this.workerUpload.onmessage = async (e) => this.handleWorkerUploadMessage(e)
  }

  private async startWorkerJob(item: DownloadQueueItem) {
    addBreadcrumb("download", "Worker job started", "info", {
      file_name: item.name,
    })
    const conversationEncryptionKeys = runtimeKeyStore.getConversationKeys()
    const projectEncryptionKeys = runtimeKeyStore.getProjectKeys()
    const buildEncryptionKeys = runtimeKeyStore.getBuildKeys()

    const tokenAuth = getAccessTokenInLocalStorage()
    if (item.isFileOne) {
      this.workerDownload?.postMessage({
        action: ACTION_DOWNLOAD_WORKER.START,
        payload: {
          itemQueue: item,
          tokenAuth,
        },
      })
    } else {
      const projectId = getParameterByName("project_id", item.apiURL)
      const conversationId = getParameterByName("conversation_id", item.apiURL)

      const privateKey: any = await getPrivateKeyByUserId()

      const response = await fetch(`${API_URL}${item.apiURL}`, {
        headers: {
          Authorization: tokenAuth,
          ...(item.dbKey ? { "x-db-key": item.dbKey } : {}),
        },
      })
      if (!response.ok) {
        const userMsg =
          response.status === 404
            ? "Download folder not found. It may have been deleted."
            : response.status === 403
            ? "You don't have permission to download this package."
            : `Failed to load download package (error ${response.status}). Please try again.`

        item.done = false
        item.errorMessage = userMsg
        showToastProgress(this.storageKey, this.queue, this.actionItem)

        const trcCode = generateTRCCode("DWL")
        captureDownloadEvent(`File list fetch failed - ${trcCode}`, {
          error_category: "DWL",
          error_type: "FETCH_FILE_LIST_FAILURE",
          ...getSentryUserContext(),
          error_code: trcCode,
          step_trail: "downloadQueue → startWorkerJob → fetch file list",
          file_url: item.apiURL,
          http_status: response.status,
          build_id: item.buildId,
        })
        this.emitDownloadJobOutcome(item, {
          success: false,
          errorMessage: userMsg,
        })
        return
      }
      let responseJson: any
      try {
        responseJson = await response.json()
      } catch (parseError: any) {
        const trcCode = generateTRCCode("DWL")
        captureDownloadEvent(`File list JSON parse failed - ${trcCode}`, {
          error_category: "DWL",
          error_type: "FILE_LIST_JSON_PARSE_ERROR",
          ...getSentryUserContext(),
          error_code: trcCode,
          step_trail: "downloadQueue → startWorkerJob → response.json()",
          file_url: item.apiURL,
          build_id: item.buildId,
        })
        item.done = false
        item.errorMessage = "Failed to load download package. Please try again."
        showToastProgress(this.storageKey, this.queue, this.actionItem)
        this.emitDownloadJobOutcome(item, {
          success: false,
          errorMessage: item.errorMessage,
        })
        return
      }
      const listFile = responseJson.data || []
      const fileFolderName = responseJson.name ? responseJson.name : ""
      const results = await Promise.all(
        listFile.map(async (file: any) => {
          const isAdditionalInfo = Boolean(
            file.keyToFolder === "additional_info" && item.buildId
          )
          let key = projectId ? projectEncryptionKeys[projectId] : ""
          let newMessages: any = null
          if (file.buffer) {
            if (!isUndefined(file.messages) && file.messages.length) {
              if (file.conversationId) {
                newMessages = await Promise.all(
                  file.messages.map(async (el) => {
                    const messageKey =
                      el.replyKeyConversation ||
                      conversationEncryptionKeys[file.conversationId]
                    return {
                      message: el.message,
                      messageKey,
                      uniqueKey: el.uniqueKey,
                    }
                  })
                )
              }
              if (
                isAdditionalInfo &&
                item.buildId &&
                buildEncryptionKeys[item.buildId]
              ) {
                newMessages = await Promise.all(
                  file.messages.map(async (el) => {
                    const messageKey = buildEncryptionKeys[item.buildId || ""]
                    return {
                      message: el.message,
                      messageKey,
                      uniqueKey: el.uniqueKey,
                    }
                  })
                )
              }
            } else {
              let newBufferKey = file.shareKey
              if (projectId) {
                const projectKey = projectEncryptionKeys[projectId]
                if (projectKey) {
                  newBufferKey = await encryptionGCMHelper.decryptContext(
                    projectKey,
                    file.shareKey
                  )
                }
              }

              if (newBufferKey === file.shareKey) {
                try {
                  newBufferKey = privateKey.decrypt(file.shareKey, "RSA-OAEP")
                } catch (error: any) {
                  addBreadcrumb(
                    "download",
                    "Share key RSA decrypt failed",
                    "warning",
                    {
                      file_name: file.file,
                      error: error?.message,
                    }
                  )
                }
              }
              if (newBufferKey !== file.shareKey) {
                key = newBufferKey
              }
            }
            if (isAdditionalInfo && item.buildId) {
              key = buildEncryptionKeys[item.buildId]
            }
          } else if (file.file && !file.buffer && file.buffer !== "") {
            if (file.shareKey) {
              let newKey = file.shareKey
              if (projectId) {
                const projectKey = projectEncryptionKeys[projectId]
                if (projectKey) {
                  newKey = await encryptionGCMHelper.decryptContext(
                    projectKey,
                    file.shareKey
                  )
                }
              }

              if (newKey !== file.shareKey) {
                key = newKey
              } else {
                try {
                  key = privateKey.decrypt(file.shareKey, "RSA-OAEP")
                } catch (error: any) {
                  addBreadcrumb(
                    "download",
                    "Share key RSA decrypt failed",
                    "warning",
                    {
                      file_name: file.file,
                      error: error?.message,
                    }
                  )
                }
              }
            } else {
              if (conversationId && file.keyToFolder === "conversation") {
                key =
                  file.replyKeyConversation ||
                  conversationEncryptionKeys[conversationId]
              }
              if (isAdditionalInfo && item.buildId) {
                key = buildEncryptionKeys[item.buildId]
              }
            }
          }
          return {
            ...file,
            messages: newMessages,
            key,
          }
        })
      )
      this.workerDownload?.postMessage({
        action: ACTION_DOWNLOAD_WORKER.START,
        payload: {
          itemQueue: item,
          tokenAuth,
          multipleFiles: results.filter((el: any) => el?.key),
          fileFolderName,
        },
      })
    }
  }

  private async startWorkerUploadJob(item: DownloadQueueItem) {
    if (!this.workerUpload || isUndefined(item.dataUploading)) return
    const projectKey =
      runtimeKeyStore.getProjectKeys()[item.dataUploading.projectId]
    if (
      item.dataUploading.type === UploadFileType.GERBER &&
      item.dataUploading.file
    ) {
      this.uploadFileGerberWorker(
        item.dataUploading,
        item.downloadId,
        projectKey,
        item.dataUploading.idComponentHistory || "",
        item.dataUploading.componentType
      )
    }
    if (
      includes(
        [
          UploadFileType.PCB_BOM,
          UploadFileType.PCB_STACKUP,
          UploadFileType.PCB_ASSEMBLY,
          UploadFileType.PCB_ATTACHMENT,
          UploadFileType.BOM,
          UploadFileType.MECHANICAL_3D,
          UploadFileType.MECHANICAL_BOM,
          UploadFileType.MECHANICAL_ATTACHMENT,
          UploadFileType.OTHER_ATTACHMENT,
        ],
        item.dataUploading.type
      ) &&
      item.dataUploading.file
    ) {
      this.uploadFileBaseWorker(
        item.dataUploading,
        item.downloadId,
        projectKey,
        item.dataUploading.idComponentHistory || "",
        item.dataUploading.componentType
      )
    }
  }

  private emitDownloadJobOutcome(
    item: DownloadQueueItem,
    args: {
      success: boolean
      errorMessage?: string
      workerError?: WorkerDownloadError | null
    }
  ) {
    const [successEv, failureEv] = getDownloadEventPair(item)
    const trc = args.success ? undefined : generateTRCCode("DWL")
    const errorDetail =
      args.errorMessage ||
      args.workerError?.message ||
      (args.success ? undefined : "unknown")
    if (args.success) {
      const base = buildDownloadTrackingPayload(item, {})
      umamiTracking(successEv, base)
      umamiTracking(EVENT.FILE_CRYPTO.DECRYPT_SUCCESS, {})
      umamiTracking(EVENT.FILE_CRYPTO.VERIFY_SUCCESS, {})
    } else {
      const base = buildDownloadTrackingPayload(item, {
        error: trc,
      })
      umamiTracking(failureEv, {
        ...base,
        error: trc,
        error_message: errorDetail,
      })
      const phase = args.workerError?.phase
      if (
        phase === "DECRYPT_GCM" ||
        phase === "DECRYPT_CBC" ||
        phase === "DECRYPT_MESSAGE"
      ) {
        umamiTracking(EVENT.FILE_CRYPTO.DECRYPT_FAILURE, { error: trc })
      } else if (phase === "VERIFY_SIGNATURE") {
        umamiTracking(EVENT.FILE_CRYPTO.VERIFY_FAILURE, { error: trc })
      }
    }
  }

  private async handleWorkerDownloadMessage(e: MessageEvent) {
    if (e.data.action === ACTION_DOWNLOAD_WORKER.FINISH) {
      const {
        dataBlob,
        messageError,
        downloadId,
        fileFolderName,
        workerError,
      } = e.data.payload
      const item = this.queue.find((i) => i.downloadId === downloadId)
      if (!item) return

      addBreadcrumb(
        "download",
        "Worker finished",
        dataBlob ? "info" : "error",
        { has_blob: !!dataBlob, file_name: item.name }
      )

      if (workerError) {
        const trcCode = generateTRCCode("DWL")
        captureDownloadEvent(
          `Worker error: ${
            (workerError as WorkerDownloadError).phase
          } - ${trcCode}`,
          {
            error_category: "DWL",
            error_type: `WORKER_ERROR_${
              (workerError as WorkerDownloadError).phase
            }`,
            ...getSentryUserContext(),
            error_code: trcCode,
            step_trail: "downloadQueue → handleWorkerDownloadMessage → FINISH",
            step_failed: (workerError as WorkerDownloadError).message,
            file_name: item.name,
            build_id: item.buildId,
            file_url: (workerError as WorkerDownloadError).file_url,
            http_status: (workerError as WorkerDownloadError).http_status,
            file_index: (workerError as WorkerDownloadError).file_index,
            skipped_files: (workerError as WorkerDownloadError).skipped_files,
          }
        )
      }

      const wErr =
        (workerError as WorkerDownloadError | null | undefined) || null

      if (item.isFileOne && item.dataFileOne) {
        await this.handleSingleFile(item, dataBlob, messageError)
      } else {
        await this.handleMultipleFile(
          item,
          item.prefixStart
            ? `${item.prefixStart} ${fileFolderName}`
            : fileFolderName,
          dataBlob,
          messageError
        )
      }

      const success = item.done === true
      this.emitDownloadJobOutcome(item, {
        success,
        errorMessage:
          item.errorMessage || messageError || wErr?.message || undefined,
        workerError: success ? null : wErr,
      })

      showToastProgress(this.storageKey, this.queue, this.actionItem)
      this.queue = this.queue.filter((i) => !i.done)
      showToastProgress(this.storageKey, this.queue, this.actionItem)
      // await this.persistQueue()
      if (this.queue.length === 0) {
        clearToastProgress(this.storageKey)
      }
    }
    if (e.data.action === ACTION_DOWNLOAD_WORKER.PROCESS) {
      const { message, downloadId, process } = e.data.payload
      const item = this.queue.find((i) => i.downloadId === downloadId)
      if (item) {
        item.processMessage = message
        item.done = false
        showToastProgress(
          this.storageKey,
          this.queue,
          this.actionItem,
          process,
          downloadId
        )
      }
    }
  }

  private async handleSingleFile(
    item: DownloadQueueItem,
    dataBlob: Blob | null,
    messageError: string
  ) {
    if (!dataBlob) {
      item.done = false
      item.errorMessage = messageError
      return
    }

    item.done = false
    item.errorMessage = ""

    try {
      await handleDownloadOneFile(
        item.dataFileOne!,
        this.storageKey,
        this.queue,
        item.downloadId,
        this.actionItem,
        dataBlob
      )
      item.done = true
    } catch (error: any) {
      item.errorMessage = error?.message || "Unknown error"
      item.done = false
      const trcCode = generateTRCCode("DWL")
      captureDownloadEvent(`Single file save failed - ${trcCode}`, {
        error_category: "DWL",
        error_type: "SINGLE_FILE_SAVE_FAILURE",
        ...getSentryUserContext(),
        error_code: trcCode,
        step_trail:
          "downloadQueue → handleSingleFile → handleDownloadOneFile catch",
        step_failed: error?.message,
        file_name: item.name,
        build_id: item.buildId,
        file_url: item.apiURL,
      })
    }
  }

  private async handleMultipleFile(
    item: DownloadQueueItem,
    fileFolderName: string,
    dataBlob: Blob | null,
    messageError: string
  ) {
    if (!dataBlob) {
      item.done = false
      item.errorMessage = messageError
      return
    }

    try {
      await handleDownloadEncrypted(
        item.downloadId,
        this.queue,
        this.storageKey,
        dataBlob,
        this.actionItem,
        fileFolderName
      )
      item.done = true
    } catch (error: any) {
      item.errorMessage = error?.message || "Unknown error"
      item.done = false
      const trcCode = generateTRCCode("DWL")
      captureDownloadEvent(`Multiple file save failed - ${trcCode}`, {
        error_category: "DWL",
        error_type: "MULTI_FILE_SAVE_FAILURE",
        ...getSentryUserContext(),
        error_code: trcCode,
        step_trail:
          "downloadQueue → handleMultipleFile → handleDownloadEncrypted catch",
        step_failed: error?.message,
        file_name: item.name,
        build_id: item.buildId,
        file_url: item.apiURL,
      })
    }
  }

  private async persistQueue() {
    await updateData(Stores.DownloadFile, {
      id: this.storageKey,
      data: this.queue,
    })
  }

  async getQueues() {
    const stored = (await getStoreData(Stores.DownloadFile)) as {
      id: string
      data: DownloadQueueItem[]
    }[]
    return Array.isArray(stored)
      ? stored.find((item) => item.id === this.storageKey)?.data || []
      : []
  }

  async restoreQueue() {
    // const originalQueue = await this.getQueues()
    // if (originalQueue.length === 0) return
    // await this.initWorkerDownload()
    // this.queue = originalQueue.filter((item) => !item.errorMessage)
    // showToastProgress(this.storageKey, this.queue, this.actionItem)
    // for (const item of originalQueue) {
    //   if (item.done || item.errorMessage) continue
    //   await this.startWorkerJob(item)
    // }
  }
  private setupBeforeUnloadWarning() {
    window.addEventListener("beforeunload", (e) => {
      const hasUploading = this.queue.length
      if (hasUploading) {
        const message =
          "Files are still being processed. Are you sure you want to leave?"
        e.preventDefault()
        e.returnValue = message // for legacy browsers
        return message
      }
    })
  }

  async checkQueueExist(downloadId: string) {
    const originalQueue = await this.getQueues()
    return originalQueue.some((item) => item.downloadId === downloadId)
  }

  private async actionItem(type: "delete", downloadId: string) {
    this.queue = this.queue.filter((q) => q.downloadId !== downloadId)
    configureStore.dispatch(finishUpload(downloadId))
    showToastProgress(this.storageKey, this.queue, this.actionItem)
    if (this.queue.length === 0) {
      clearToastProgress(this.storageKey)
    }
  }

  private async handleWorkerUploadMessage(e: MessageEvent) {
    if (e.data.action !== ACTION_WORKER.FINISH) return
    const fileErrors: string[] = e.data.payload.fileErrors ?? []
    if (fileErrors.length > 0) {
      addBreadcrumb("download", "Worker upload file errors", "warning", {
        errors: fileErrors,
        download_id: e.data.payload.downloadId,
      })
      const isSessionExpired = fileErrors.some((msg) =>
        msg?.includes("Private key")
      )
      if (isSessionExpired) {
        const trcCode = generateTRCCode("DWL")
        captureDownloadEvent(`Session expired during upload - ${trcCode}`, {
          error_category: "DWL",
          error_type: "SESSION_EXPIRED_UPLOAD",
          ...getSentryUserContext(),
          error_code: trcCode,
          step_trail:
            "downloadQueue → handleWorkerUploadMessage → Private key not found",
          step_failed: fileErrors[0],
        })
        const item = this.queue.find(
          (i) => i.downloadId === e.data.payload.downloadId
        )
        if (item) {
          item.errorMessage =
            "Your session has expired. You will be redirected to log in again."
          item.done = false
          showToastProgress(this.storageKey, this.queue, this.actionItem)
        }
        setTimeout(() => {
          clearLocalStorage()
          window.location.href = "/login"
        }, 2000)
        return
      }
    }
    const item = this.queue.find(
      (i) => i.downloadId === e.data.payload?.downloadId && isUndefined(i.done)
    )
    if (item) {
      const result = await handleUploadFile(
        e.data.payload,
        item,
        this.queue,
        this.storageKey,
        this.actionItem
      )
      if (result === "success") {
        item.done = true
        item.errorMessage = ""
      } else {
        item.done = false
        item.errorMessage = result
      }
      configureStore.dispatch(finishUpload(e.data.payload?.downloadId))
      showToastProgress(this.storageKey, this.queue, this.actionItem)
      this.queue = this.queue.filter((i) => !i.done)
      showToastProgress(this.storageKey, this.queue, this.actionItem)
      // await this.persistQueue()
      if (this.queue.length === 0) {
        clearToastProgress(this.storageKey)
      }
    }
  }

  private async uploadFileGerberWorker(
    dataRequest: any,
    downloadId: string,
    projectKey: string,
    idComponentHistory: string,
    componentType: number
  ) {
    const request = new FormData()
    request.append("file", dataRequest.file)
    try {
      const responseSvg = await Axios.post(`/api/pcb/to-svg?loading=0`, request)

      this.workerUpload?.postMessage({
        action: ACTION_WORKER.START,
        payload: {
          isRawFile: true,
          files: [dataRequest.file],
          dataSvgs: responseSvg.data.data,
          downloadId,
          encryptionKey: projectKey,
          token: getAccessTokenInLocalStorage(),
          componentHistoryId: idComponentHistory,
          componentType,
          userId: getUserId() || "",
        },
      })
    } catch (error) {
      const item = this.queue.find((i) => i.downloadId === downloadId)
      if (item) {
        item.done = false
        item.errorMessage =
          "Failed to process PCB file. Please verify the file format and try again."
        // await this.persistQueue()
        showToastProgress(this.storageKey, this.queue, this.actionItem)
      }
    }
  }
  private async uploadFileBaseWorker(
    dataRequest: any,
    downloadId: string,
    projectKey: string,
    idComponentHistory: string,
    componentType: number
  ) {
    this.workerUpload?.postMessage({
      action: ACTION_WORKER.START,
      payload: {
        files: [dataRequest.file],
        isRawFile: true,
        downloadId,
        encryptionKey: projectKey,
        token: getAccessTokenInLocalStorage(),
        componentHistoryId: idComponentHistory,
        componentType,
        userId: getUserId() || "",
      },
    })
  }
}

export const downloadQueueManager = new DownloadQueueManager()
export { getTabId }
